/**
 * Print HTML template for Accounting reports.
 *
 * Pure template builder: takes already-rendered row markup and
 * letterhead data, returns a full standalone HTML page string ready
 * to be written into a new window and printed. No DOM access, no
 * window handling, no business logic.
 *
 * Design (v4, approved 2026-10-22):
 *   - Letterhead at top: left company block (hidden entirely when
 *     company name is empty), right report block with REPORT kicker,
 *     title, and stacked meta list.
 *   - All structural lines are 1px soft gray (#E7EAF0). No thick
 *     black bars.
 *   - Three-level text hierarchy:
 *       primary   #0E1A1A  names, titles, template rows, amounts, meta values
 *       secondary #263238  addresses, city line, phone, email
 *       tertiary  #3A4A52  REPORT kicker, meta labels, table headers, footer
 *   - Zebra row fill #FBFCFD. US Letter landscape, 0.5in margins.
 *
 * Reusable: future reports (Rules, Bank transactions, Invoices) call
 * this same function with their own row markup and the matching
 * COLUMNS set.
 */

import { COLUMNS, type ColumnKey } from "../recurringColumns";

/** Escapes HTML special characters. Exported so other print files reuse it. */
export function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Company letterhead data. Any field can be empty: the template
 * only renders lines that have content. When companyName is empty,
 * the entire left letterhead block is omitted (right side spans
 * full width instead of showing fake placeholder text).
 */
export interface LetterheadData {
  companyName: string;
  address1?: string;
  address2?: string;
  cityLine?: string;
  phone?: string;
  email?: string;
}

export interface BuildPrintHtmlOptions {
  /** Report heading shown on the right side of the letterhead. */
  reportTitle: string;
  /** View filter label (for example, "Reminder List"). */
  viewModeLabel: string;
  /** Row count label (for example, "14 templates"). */
  countLabel: string;
  /** Formatted generation timestamp (for example, "Oct 22, 2026 at 1:30 PM"). */
  generatedAt: string;
  /** Letterhead data; companyName empty hides the left block entirely. */
  letterhead: LetterheadData;
  /** Visible column set for the table head rendering. */
  visibleColumns: ReadonlySet<ColumnKey>;
  /** Pre-rendered <tr>...</tr> row markup (already escaped). */
  rows: string;
}

/**
 * Builds the full HTML page string for printing. The caller is
 * responsible for writing this string into a new window and
 * invoking its print() method.
 */
export function buildPrintHtml(options: BuildPrintHtmlOptions): string {
  const {
    reportTitle,
    viewModeLabel,
    countLabel,
    generatedAt,
    letterhead,
    visibleColumns,
    rows,
  } = options;

  const printableCols = COLUMNS.filter(
    (c) => visibleColumns.has(c.key) && c.key !== "action",
  );

  const headerCells = printableCols
    .map(
      (c) =>
        `<th${c.rightAlign ? ' style="text-align:right"' : ""}>${escapeHtml(c.label)}</th>`,
    )
    .join("");

  const companyBlock = letterhead.companyName
    ? renderCompanyBlock(letterhead)
    : "";

  const titleTag = letterhead.companyName
    ? `${escapeHtml(reportTitle)} - ${escapeHtml(letterhead.companyName)}`
    : escapeHtml(reportTitle);

  const footerLeft = `Page 1 of 1 &middot; ${escapeHtml(generatedAt)}`;
  const footerRight = `Printed from Novala`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${titleTag}</title>
  <style>
    @page { size: letter landscape; margin: 0.5in; }
    html, body { margin: 0; padding: 0; background: #ffffff; }
    body {
      font-family: -apple-system, "Segoe UI", Roboto, sans-serif;
      color: #0E1A1A;
      padding: 28px;
    }

    /* Letterhead */
    .letterhead {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      padding-bottom: 18px;
      margin-bottom: 22px;
      border-bottom: 1px solid #E7EAF0;
      gap: 32px;
    }
    .letterhead-left { line-height: 1.5; }
    .company-name {
      font-size: 22px;
      font-weight: 800;
      color: #0E1A1A;
      margin: 0 0 6px;
      letter-spacing: -0.015em;
    }
    .company-address,
    .company-contact {
      font-size: 14px;
      color: #263238;
      font-weight: 500;
      margin: 0;
    }
    .company-contact { margin-top: 2px; }

    .report-side { text-align: right; }
    .report-side.full { text-align: left; }
    .report-kicker {
      font-size: 11px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: #3A4A52;
      font-weight: 700;
      margin: 0 0 4px;
    }
    .report-title {
      font-size: 26px;
      font-weight: 800;
      margin: 0 0 8px;
      letter-spacing: -0.02em;
      color: #0E1A1A;
    }
    .report-meta {
      font-size: 13.5px;
      color: #0E1A1A;
      font-weight: 500;
      line-height: 1.7;
    }
    .report-meta .row { display: block; }
    .report-meta strong { color: #3A4A52; font-weight: 700; }

    /* Table */
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 14.5px;
      table-layout: auto;
    }
    th {
      text-align: left;
      font-size: 11.5px;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      font-weight: 700;
      color: #3A4A52;
      padding: 12px 10px;
      border-bottom: 1px solid #E7EAF0;
      background: transparent;
      white-space: nowrap;
    }
    td {
      padding: 12px 10px;
      border-bottom: 1px solid #E7EAF0;
      font-size: 14.5px;
      color: #0E1A1A;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    tbody tr:nth-child(even) td { background: #FBFCFD; }
    .name-cell { font-weight: 700; color: #0E1A1A; }
    .amount-cell {
      text-align: right;
      font-family: ui-monospace, Menlo, monospace;
      font-weight: 700;
      color: #0E1A1A;
    }

    /* Footer */
    .foot {
      margin-top: 28px;
      padding-top: 14px;
      border-top: 1px solid #E7EAF0;
      font-size: 12px;
      color: #3A4A52;
      font-weight: 500;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .foot .brand-mark {
      font-size: 11px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: 700;
      color: #3A4A52;
    }

    @media print {
      body { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="letterhead">
    ${companyBlock}
    <div class="report-side${letterhead.companyName ? "" : " full"}">
      <p class="report-kicker">Report</p>
      <h2 class="report-title">${escapeHtml(reportTitle)}</h2>
      <div class="report-meta">
        <span class="row"><strong>View:</strong> ${escapeHtml(viewModeLabel)}</span>
        <span class="row"><strong>Count:</strong> ${countLabel}</span>
        <span class="row"><strong>Generated:</strong> ${escapeHtml(generatedAt)}</span>
      </div>
    </div>
  </div>

  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="foot">
    <span>${footerLeft}</span>
    <span class="brand-mark">${footerRight}</span>
  </div>

  <script>
    (function () {
      window.onafterprint = function () { window.close(); };
      window.addEventListener("load", function () {
        setTimeout(function () { window.print(); }, 150);
      });
    })();
  </script>
</body>
</html>`;
}

/** Renders the left company block. Called only when companyName is non-empty. */
function renderCompanyBlock(letterhead: LetterheadData): string {
  const lines: string[] = [];
  lines.push(
    `<p class="company-name">${escapeHtml(letterhead.companyName)}</p>`,
  );
  if (letterhead.address1) {
    lines.push(
      `<p class="company-address">${escapeHtml(letterhead.address1)}</p>`,
    );
  }
  if (letterhead.address2) {
    lines.push(
      `<p class="company-address">${escapeHtml(letterhead.address2)}</p>`,
    );
  }
  if (letterhead.cityLine) {
    lines.push(
      `<p class="company-address">${escapeHtml(letterhead.cityLine)}</p>`,
    );
  }
  const contactParts: string[] = [];
  if (letterhead.phone) contactParts.push(escapeHtml(letterhead.phone));
  if (letterhead.email) contactParts.push(escapeHtml(letterhead.email));
  if (contactParts.length > 0) {
    lines.push(
      `<p class="company-contact">${contactParts.join(" &middot; ")}</p>`,
    );
  }
  return `<div class="letterhead-left">${lines.join("")}</div>`;
}